import {
  AfterViewInit,
  Directive,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { FormControlName, NgControl, UntypedFormControl } from '@angular/forms';
import { NgrxFormControlDirective } from 'ngrx-forms';
import { startWith, Subscription } from 'rxjs';

import { hasValue } from '../../util';
import { PcsErrorsToken } from './errors-token';

@Directive()
export abstract class BaseErrorsDirective implements OnChanges, OnDestroy, AfterViewInit {
  private static readonly REQUIRED_CLASS = 'pcs-required';

  @Input() ngrxFormControlState: NgrxFormControlDirective<any>;
  @Input() formControlName: FormControlName;
  @Input() formControl: UntypedFormControl;

  private control = null;
  private attached = false;
  private readonly subscription = new Subscription();

  private get errors() {
    return this.control && this.control.errors;
  }

  private get dirty() {
    return this.control && (this.control.dirty || this.control.isDirty);
  }

  private get displayErrors() {
    return hasValue(this.errors) && this.dirty;
  }

  protected constructor(
    protected readonly injector: Injector,
    protected readonly element: ElementRef,
    protected readonly renderer: Renderer2
  ) {
  }

  protected abstract attach();

  protected abstract detach();

  ngAfterViewInit() {
    if (this.formControl || this.formControlName) {
      this.handleControlErrors();
    } else if (this.ngrxFormControlState) {
      this.control = this.ngrxFormControlState as any;
      this.handleErrors();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ngrxFormControlState && !changes.ngrxFormControlState.isFirstChange()) {
      this.control = this.ngrxFormControlState as any;
      this.handleErrors();
    }

    if (
      (changes.formControl && !changes.formControl.isFirstChange())
      || (changes.formControlName && !changes.formControlName.isFirstChange())
    ) {
      this.handleControlErrors();
    }
  }

  ngOnDestroy() {
    this.detachInternal();
    this.subscription.unsubscribe();
  }

  private handleControlErrors() {
    this.control = this.injector.get<NgControl>(NgControl, null);
    this.subscription.add(this.control.statusChanges.pipe(startWith(null)).subscribe(() => {
      this.handleErrors();
    }));
  }

  private handleErrors() {
    this.handleRequiredControl();
    if (this.displayErrors) {
      this.attachInternal();
    } else {
      this.detachInternal();
    }
  }

  private handleRequiredControl() {
    if (this.isRequired()) {
      this.renderer.addClass(this.element.nativeElement, BaseErrorsDirective.REQUIRED_CLASS);
    } else {
      this.renderer.removeClass(this.element.nativeElement, BaseErrorsDirective.REQUIRED_CLASS);
    }
  }

  private attachInternal() {
    this.detachInternal();
    if (!this.attached) {
      this.attach();
      this.attached = true;
    }
  }

  private detachInternal() {
    if (this.attached) {
      this.detach();
      this.attached = false;
    }
  }

  protected errorsComponentInjector() {
    return Injector.create({
      parent: this.injector,
      providers: [
        {provide: PcsErrorsToken, useValue: this.errors}
      ]
    });
  }

  private isRequired() {
    return !!(this.errors && (this.errors.required || this.errors.valdrRequired));
  }
}
